import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Calendar } from 'lucide-react';
import './ClicksignEnvios.css';

const API_BASE = '/api';

function authHeaders() {
  return {
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json'
  };
}

async function apiFetch(endpoint, options = {}) {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: { ...authHeaders(), ...(options.headers || {}) }
  });

  const text = await response.text();
  let data = {};

  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { error: text };
  }

  if (!response.ok) throw new Error(data.error || 'Erro na requisição');
  return data;
}

// Upload de arquivo: NÃO pode mandar Content-Type JSON; o navegador define o
// multipart (com o boundary) sozinho quando o corpo é um FormData.
async function apiUpload(endpoint, formData) {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    body: formData
  });

  const text = await response.text();
  let data = {};

  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { error: text };
  }

  if (!response.ok) {
    if (response.status === 413) throw new Error('Arquivo grande demais para o servidor');
    throw new Error(data.error || 'Erro na requisição');
  }
  return data;
}

function criarFormDataArquivo(arquivos, nomeDocumento, deliveryMethod, signers) {
  const formData = new FormData();
  arquivos.forEach(item => formData.append('arquivos', item.file));
  if (nomeDocumento && nomeDocumento.trim()) formData.append('nomeDocumento', nomeDocumento.trim());
  formData.append('deliveryMethod', deliveryMethod);
  formData.append('signers', JSON.stringify(signers));
  return formData;
}

function formatCpf(value) {
  const d = String(value || '').replace(/\D/g, '').slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

function normalizePhone(value) {
  let digits = String(value || '').replace(/\D/g, '');

  if (digits.startsWith('55') && (digits.length === 12 || digits.length === 13)) {
    digits = digits.slice(2);
  }

  return digits;
}

function toDateInput(value) {
  if (!value) return '';

  const text = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);

  const brDate = text.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  return brDate ? `${brDate[3]}-${brDate[2]}-${brDate[1]}` : '';
}

function makeSigner(values = {}) {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    name: '',
    email: '',
    phone_number: '',
    cpf: '',
    birthday: '',        // formato interno aaaa-mm-dd (é o que vai pro backend)
    birthdayDisplay: '', // formato digitado dd/mm/aaaa
    ...values
  };
}


export default function ClicksignEnvios({ tipoDocumento }) {
  const location = useLocation();
  const clienteInicial = location.state?.client || null;

  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [modo, setModo] = useState(
    clienteInicial ? 'formulario' : 'arquivo'
  ); // 'formulario' (desativado) | 'arquivo'
  const [arquivos, setArquivos] = useState([]);
  const [nomeDocumento, setNomeDocumento] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState('whatsapp');
  const [signers, setSigners] = useState(() => [makeSigner()]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);

  useEffect(() => {
    if (!clienteInicial) return;

    const nome =
      clienteInicial.nome ||
      clienteInicial.full_name ||
      clienteInicial.titular_plano_nome ||
      '';

    const telefone =
      clienteInicial.telefone_celular ||
      clienteInicial.phone ||
      clienteInicial.telefone ||
      '';

    const cpf =
      clienteInicial.cpf ||
      clienteInicial.titular_plano_cpf ||
      '';

    setSigners([
      makeSigner({
        name: nome,
        email: clienteInicial.email || '',
        phone_number: telefone,
        cpf,
        birthday: toDateInput(
          clienteInicial.birth_date ||
          clienteInicial.titular_plano_nascimento ||
          ''
        )
      })
    ]);

    setNomeDocumento(`Contrato CPFL - ${nome}`);
  }, [clienteInicial?.id]);

  useEffect(() => {
    if (!clienteInicial) return;

    const nome = clienteInicial.full_name || clienteInicial.titular_plano_nome || '';
    const telefone = clienteInicial.telefone_celular || clienteInicial.phone || '';
    const cpf = clienteInicial.cpf || clienteInicial.cnpj || '';

    setSigners([
      makeSigner({
        name: nome,
        email: clienteInicial.email || '',
        phone_number: telefone,
        cpf,
        birthday: toDateInput(
          clienteInicial.titular_plano_nascimento || clienteInicial.birth_date
        )
      })
    ]);

    setNomeDocumento(`Contrato Adesão - ${nome}`);
  }, [clienteInicial?.id]);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const isCancelamento = tipoDocumento === 'termo_cancelamento';
  const title = isCancelamento ? 'Termo de Cancelamento' : 'Contrato Adesão';
  const category = isCancelamento ? 'cancelamento' : 'contrato';

  const filteredTemplates = useMemo(
    () => templates.filter(template => template.categoria === category),
    [templates, category]
  );

  useEffect(() => {
    let active = true;

    async function loadTemplates() {
      try {
        setLoadingTemplates(true);
        const data = await apiFetch('/document-templates');
        if (active) setTemplates(Array.isArray(data) ? data : []);
      } catch (err) {
        if (active) setError(err.message);
      } finally {
        if (active) setLoadingTemplates(false);
      }
    }

    loadTemplates();
    return () => { active = false; };
  }, []);


  const FORMATOS_ACEITOS = /\.(pdf|jpe?g|png|docx?)$/i;
  const LIMITE_ARQUIVO = 10 * 1024 * 1024;
  const LIMITE_TOTAL = 40 * 1024 * 1024;
  const MAX_ARQUIVOS = 8;

  function escolherArquivos(event) {
    const novos = Array.from(event.target.files || []);
    event.target.value = '';
    if (!novos.length) return;

    setError('');
    const juntos = [...arquivos];
    let total = juntos.reduce((soma, item) => soma + item.file.size, 0);
    let aviso = '';

    for (const file of novos) {
      if (!FORMATOS_ACEITOS.test(file.name)) {
        aviso = `"${file.name}" não é um formato aceito (use PDF, JPG, PNG, DOC ou DOCX)`;
        continue;
      }
      if (file.size > LIMITE_ARQUIVO) {
        aviso = `"${file.name}" passa de 10 MB`;
        continue;
      }
      if (juntos.some(item => item.file.name === file.name && item.file.size === file.size)) continue;
      if (juntos.length >= MAX_ARQUIVOS) {
        aviso = `Máximo de ${MAX_ARQUIVOS} arquivos por envio`;
        break;
      }
      if (total + file.size > LIMITE_TOTAL) {
        aviso = 'O total dos arquivos passa de 40 MB';
        continue;
      }
      total += file.size;
      juntos.push({ id: `${Date.now()}-${Math.random().toString(36).slice(2)}`, file });
    }

    setArquivos(juntos);
    if (aviso) setError(aviso);
  }

  function removerArquivo(id) {
    setArquivos(atual => atual.filter(item => item.id !== id));
  }

  function moverArquivo(index, delta) {
    setArquivos(atual => {
      const destino = index + delta;
      if (destino < 0 || destino >= atual.length) return atual;
      const copia = [...atual];
      [copia[index], copia[destino]] = [copia[destino], copia[index]];
      return copia;
    });
  }

  function updateSigner(id, field, value) {
    setSigners(current =>
      current.map(signer =>
        signer.id === id ? { ...signer, [field]: value } : signer
      )
    );
  }

  function updateBirthday(id, rawValue) {
    const digits = String(rawValue || '').replace(/\D/g, '').slice(0, 8);

    let display = digits;
    if (digits.length >= 5) display = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    else if (digits.length >= 3) display = `${digits.slice(0, 2)}/${digits.slice(2)}`;

    let iso = '';
    if (digits.length === 8) {
      const dd = digits.slice(0, 2);
      const mm = digits.slice(2, 4);
      const yyyy = digits.slice(4);
      const data = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
      const valida =
        !isNaN(data.getTime()) &&
        data.getFullYear() === Number(yyyy) &&
        data.getMonth() + 1 === Number(mm) &&
        data.getDate() === Number(dd) &&
        data.getTime() <= Date.now();
      if (valida) iso = `${yyyy}-${mm}-${dd}`;
    }

    setSigners(current =>
      current.map(signer =>
        signer.id === id ? { ...signer, birthdayDisplay: display, birthday: iso } : signer
      )
    );
  }

  function hojeISO() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  function abrirCalendario(id) {
    const campo = document.getElementById(`clicksign-data-${id}`);
    if (!campo) return;
    if (typeof campo.showPicker === 'function') {
      try {
        campo.showPicker();
        return;
      } catch (e) {
        // navegador sem suporte: cai no fallback abaixo
      }
    }
    campo.focus();
    campo.click();
  }

  function escolherDataNoCalendario(id, iso) {
    setSigners(current =>
      current.map(signer => {
        if (signer.id !== id) return signer;
        if (!iso) return { ...signer, birthday: '', birthdayDisplay: '' };
        const [yyyy, mm, dd] = iso.split('-');
        return { ...signer, birthday: iso, birthdayDisplay: `${dd}/${mm}/${yyyy}` };
      })
    );
  }

  function buildDados(client, signer) {
    const dateValue =
      signer.birthday ||
      client.titular_plano_nascimento ||
      client.birth_date ||
      '';

    return {
      nome: signer.name || client.nome,
      full_name: signer.name || client.nome,
      cpf: signer.cpf || client.cpf || '',
      rg: client.rg || client.titular_plano_rg || '',
      email: signer.email || client.email || '',
      telefone: signer.phone_number || client.telefone || '',
      telefone_celular: signer.phone_number || client.telefone || '',
      telefone_residencial: client.telefone_residencial || '',
      data_nascimento: dateValue,
      birthday: dateValue,
      birth_date: dateValue,
      cidade: client.city || '',
      estado: client.state || '',
      endereco: client.address || '',
      cep: client.zip_code || '',
      plano: client.plan_name || '',
      plano_nome: client.plan_name || '',
      plano_valor: client.plan_value || ''
    };
  }

  async function handleSubmit(event) {
    event.preventDefault();


    if (modo === 'formulario' && !templateId) {
      setError('Selecione um modelo de documento');
      return;
    }

    if (modo === 'arquivo' && arquivos.length === 0) {
      setError('Selecione ao menos um arquivo para enviar para assinatura');
      return;
    }

    if (!signers.length || signers.some(signer => !signer.name.trim())) {
      setError('Informe o nome de cada signatário');
      return;
    }

    if (signers.some(signer => signer.birthdayDisplay && !signer.birthday)) {
      setError('Data de nascimento inválida. Use o formato dd/mm/aaaa');
      return;
    }

    if (deliveryMethod === 'whatsapp') {
      const invalidPhone = signers.some(
        signer => !/^\d{10,11}$/.test(normalizePhone(signer.phone_number))
      );

      if (invalidPhone) {
        setError('Informe para cada signatário um telefone com DDD (10 ou 11 dígitos)');
        return;
      }
    } else if (signers.some(signer => !signer.email.trim())) {
      setError('Informe o e-mail de cada signatário');
      return;
    }

    try {
      setSending(true);
      setError('');
      setResult(null);

      const preparedSigners = signers.map(signer => ({
        name: signer.name.trim(),
        email: signer.email.trim(),
        phone_number: normalizePhone(signer.phone_number),
        cpf: signer.cpf.trim(),
        birthday: signer.birthday
      }));

      const firstSigner = preparedSigners[0];

      const data = modo === 'arquivo'
        ? await apiUpload(
            '/clicksign/enviar-arquivo',
            criarFormDataArquivo(
              arquivos,
              nomeDocumento,
              deliveryMethod,
              preparedSigners
            )
          )
        : await apiFetch('/clicksign/preencher-contrato-cpfl', {
            method: 'POST',
            body: JSON.stringify({
              deliveryMethod,
              cliente: clienteInicial || {
                nome: firstSigner.name,
                full_name: firstSigner.name,
                email: firstSigner.email || '',
                telefone_celular: firstSigner.phone_number || '',
                cpf: firstSigner.cpf || '',
                birth_date: firstSigner.birthday || ''
              },
              signers: preparedSigners
            })
          });

      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="clicksign-page">
      <header className="clicksign-header">
        <div>
          <span className="clicksign-eyebrow">CLICK SIGN</span>
          <h1>Envio {title}</h1>
          <p>Preencha os dados do signatário e envie o documento para assinatura digital.</p>
        </div>
        <span className="clicksign-status">ClickSign configurada</span>
      </header>

      {error && (
        <div className="clicksign-alert clicksign-alert-error" role="alert">
          {error}
        </div>
      )}

      {result ? (
        <section className="clicksign-result">
          <h2>
            {result.envelope_sent
              ? 'Envelope enviado para assinatura'
              : 'Envelope criado; confira o status do envio'}
          </h2>

          <p><strong>Cliente:</strong> {result.cliente?.nome || result.signers?.[0]?.name || '—'}</p>
          <p>
            <strong>Canal ClickSign:</strong>{' '}
            {result.delivery_method === 'whatsapp' ? 'WhatsApp' : 'E-mail'}
          </p>
          <p><strong>Envelope:</strong> {result.envelope_id}</p>
          {result.notify_status && (
            <p><strong>Notificação:</strong> {result.notify_status}</p>
          )}
          {result.send_error && (
            <p><strong>Motivo:</strong> {result.send_error}</p>
          )}
          {result.signers?.filter(signer => !signer.success).map(signer => (
            <p key={signer.name}><strong>Erro no signatário {signer.name}:</strong> {signer.error}</p>
          ))}

          {result.signers?.filter(signer => signer.success).map(signer => (
            <div className="clicksign-link-box" key={signer.signer_key}>
              <div>
                <strong>{signer.name}</strong>
                <span>{signer.phone_number || signer.email}</span>
              </div>
              <a href={signer.sign_url} target="_blank" rel="noopener noreferrer">
                Abrir link
              </a>
            </div>
          ))}

          <button
            type="button"
            className="clicksign-secondary-button"
            onClick={() => { setResult(null); setArquivos([]); setNomeDocumento(''); }}
          >
            Fazer novo envio
          </button>
        </section>
      ) : (
        <form className="clicksign-card" onSubmit={handleSubmit}>

          <div className="clicksign-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              className={`clicksign-tab ${modo === 'formulario' ? 'is-active' : ''}`}
              onClick={() => {
                setModo('formulario');
                setError('');
              }}
            >
              Preencher Formulário
            </button>
            <button
              type="button"
              role="tab"
              className={`clicksign-tab ${modo === 'arquivo' ? 'is-active' : ''}`}
              onClick={() => { setModo('arquivo'); setError(''); }}
            >
              Enviar Arquivo Pronto
            </button>
          </div>

          {modo === 'arquivo' && (
            <section className="clicksign-section">
              <label className="clicksign-label" htmlFor="clicksign-arquivo">
                Documentos (PDF, imagem ou Word) *
              </label>
              <input
                id="clicksign-arquivo"
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,application/pdf,image/jpeg,image/png"
                onChange={escolherArquivos}
                style={{ display: 'none' }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <label
                  htmlFor="clicksign-arquivo"
                  style={{
                    display: 'inline-block',
                    padding: '8px 16px',
                    border: '1px solid #cbd5e1',
                    borderRadius: 8,
                    background: '#ffffff',
                    color: '#334155',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Escolher arquivos
                </label>
                <span style={{ fontSize: 14, color: '#64748b' }}>
                  {arquivos.length === 0
                    ? 'Nenhum arquivo selecionado'
                    : `${arquivos.length} arquivo(s) selecionado(s)`}
                </span>
              </div>

              {arquivos.length > 0 && (
                <ol className="clicksign-file-list">
                  {arquivos.map((item, index) => (
                    <li key={item.id}>
                      <span className="clicksign-file-name">{index + 1}. {item.file.name}</span>
                      <span className="clicksign-file-size">{Math.max(1, Math.round(item.file.size / 1024))} KB</span>
                      <button type="button" title="Subir" disabled={index === 0} onClick={() => moverArquivo(index, -1)}>↑</button>
                      <button type="button" title="Descer" disabled={index === arquivos.length - 1} onClick={() => moverArquivo(index, 1)}>↓</button>
                      <button type="button" title="Remover" onClick={() => removerArquivo(item.id)}>✕</button>
                    </li>
                  ))}
                </ol>
              )}

              {arquivos.length > 1 && (
                <p className="clicksign-help">
                  Os {arquivos.length} arquivos serão unidos em um único PDF, na ordem acima.
                </p>
              )}

              {arquivos.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <label className="clicksign-label" htmlFor="clicksign-nome-doc">Nome do documento (opcional)</label>
                  <input
                    id="clicksign-nome-doc"
                    type="text"
                    value={nomeDocumento}
                    onChange={event => setNomeDocumento(event.target.value)}
                    placeholder="Ex.: Contrato e documentos do titular"
                  />
                </div>
              )}
            </section>
          )}

          <section className="clicksign-section" style={modo === 'arquivo' ? { display: 'none' } : undefined}>
            <label className="clicksign-label" htmlFor="clicksign-template">
              Modelo do documento
            </label>

            {loadingTemplates ? (
              <p className="clicksign-help">Carregando modelos...</p>
            ) : (
              <select
                id="clicksign-template"
                value={templateId}
                onChange={event => setTemplateId(event.target.value)}
              >
                <option value="">Selecione um modelo de {title.toLowerCase()}</option>
                {filteredTemplates.map(template => (
                  <option value={template.id} key={template.id}>{template.nome}</option>
                ))}
              </select>
            )}

            {!loadingTemplates && filteredTemplates.length === 0 && (
              <p className="clicksign-help">
                Nenhum modelo cadastrado para esta categoria. Cadastre-o em Administração → Modelos de Documentos.
              </p>
            )}
          </section>

          <section className="clicksign-section clicksign-delivery-section">
            <span className="clicksign-label">Tipo de envio</span>
            <div className="clicksign-radio-group">
              <label className={deliveryMethod === 'whatsapp' ? 'is-selected' : ''}>
                <input
                  type="radio"
                  name="deliveryMethod"
                  checked={deliveryMethod === 'whatsapp'}
                  onChange={() => setDeliveryMethod('whatsapp')}
                />
                Envio WhatsApp
              </label>

              <label className={deliveryMethod === 'email' ? 'is-selected' : ''}>
                <input
                  type="radio"
                  name="deliveryMethod"
                  checked={deliveryMethod === 'email'}
                  onChange={() => setDeliveryMethod('email')}
                />
                Envio E-mail
              </label>
            </div>
          </section>

          <div className="clicksign-signers">
            {signers.map((signer, index) => (
              <section className="clicksign-signer-card" key={signer.id}>
                <div className="clicksign-signer-heading">
                  <h2>Signatário {index + 1}</h2>
                  {signers.length > 1 && (
                    <button
                      type="button"
                      className="clicksign-text-button"
                      onClick={() => setSigners(current => current.filter(item => item.id !== signer.id))}
                    >
                      Remover
                    </button>
                  )}
                </div>

                <div className="clicksign-fields">
                  <label>
                    {deliveryMethod === 'whatsapp' ? 'WhatsApp *' : 'E-mail *'}
                    <input
                      type={deliveryMethod === 'whatsapp' ? 'tel' : 'email'}
                      value={deliveryMethod === 'whatsapp' ? signer.phone_number : signer.email}
                      onChange={event =>
                        updateSigner(
                          signer.id,
                          deliveryMethod === 'whatsapp' ? 'phone_number' : 'email',
                          event.target.value
                        )
                      }
                      placeholder={deliveryMethod === 'whatsapp' ? '(11) 99999-9999' : 'cliente@exemplo.com'}
                      required
                    />
                  </label>

                  <label className="clicksign-field-full">
                    Nome do Signatário *
                    <input
                      value={signer.name}
                      onChange={event => updateSigner(signer.id, 'name', event.target.value)}
                      placeholder="Nome completo"
                      required
                    />
                  </label>

                  <label>
                    CPF (opcional)
                    <input
                      value={signer.cpf}
                      onChange={event => updateSigner(signer.id, 'cpf', formatCpf(event.target.value))}
                      placeholder="000.000.000-00"
                      inputMode="numeric"
                      maxLength={14}
                    />
                  </label>

                  <label>
                    Data de nascimento (opcional)
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={signer.birthdayDisplay}
                        onChange={event => updateBirthday(signer.id, event.target.value)}
                        placeholder="dd/mm/aaaa"
                        maxLength={10}
                        style={{ paddingRight: 42 }}
                      />
                      <button
                        type="button"
                        onClick={() => abrirCalendario(signer.id)}
                        title="Escolher data no calendário"
                        aria-label="Escolher data no calendário"
                        style={{
                          position: 'absolute',
                          right: 8,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          width: 28,
                          height: 28,
                          minWidth: 0,
                          padding: 0,
                          margin: 0,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: 'none',
                          borderRadius: 6,
                          background: 'transparent',
                          color: '#64748b',
                          cursor: 'pointer'
                        }}
                      >
                        <Calendar size={18} />
                      </button>
                      <input
                        type="date"
                        id={`clicksign-data-${signer.id}`}
                        value={signer.birthday}
                        max={hojeISO()}
                        onChange={event => escolherDataNoCalendario(signer.id, event.target.value)}
                        tabIndex={-1}
                        aria-hidden="true"
                        style={{
                          position: 'absolute',
                          right: 0,
                          bottom: 0,
                          width: 1,
                          height: 1,
                          minHeight: 0,
                          padding: 0,
                          border: 0,
                          opacity: 0,
                          pointerEvents: 'none'
                        }}
                      />
                    </div>
                  </label>
                </div>
              </section>
            ))}
          </div>

          <button
            type="button"
            className="clicksign-add-person"
            onClick={() => setSigners(current => [...current, makeSigner()])}
          >
            + Adicionar pessoa
          </button>

          <div className="clicksign-actions">
            <button
              type="submit"
              className="clicksign-primary-button"
              disabled={sending || (modo === 'formulario' ? (loadingTemplates || !templateId) : arquivos.length === 0) || signers.some(signer =>
                !signer.name.trim() ||
                (deliveryMethod === 'whatsapp'
                  ? !/^\d{10,11}$/.test(normalizePhone(signer.phone_number))
                  : !signer.email.trim())
              )}
            >
              {sending ? 'Enviando para assinatura...' : 'Enviar para Assinatura'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
